import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import API_CONFIG from '../utils/apiConfig';
import './EnquiryForm.css';

// reCAPTCHA v2 explicit render: use onload + render=explicit so widget shows reliably
const RECAPTCHA_V2_SCRIPT_BASE = 'https://www.google.com/recaptcha/api.js';

const MIN_FORM_SECONDS = 3;

// Global callback name for reCAPTCHA onload (required for ?onload= query param)
const RECAPTCHA_ONLOAD_NAME = '__paycisRecaptchaOnLoad';

const EnquiryForm = () => {
  const navigate = useNavigate();
  const recaptchaContainerRef = useRef(null);
  const recaptchaWidgetIdRef = useRef(null);
  const formLoadTimeRef = useRef(Date.now());
  const [formData, setFormData] = useState({
    email: '',
    name_company: '',
    message: '',
    website: '' // honeypot – leave empty; bots often fill it
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState('');
  const [recaptchaReady, setRecaptchaReady] = useState(false);

  // Fetch reCAPTCHA site key and load v2 script with explicit render + onload
  useEffect(() => {
    let cancelled = false;
    window[RECAPTCHA_ONLOAD_NAME] = () => {
      if (!cancelled) setRecaptchaReady(true);
    };
    const loadRecaptcha = async () => {
      try {
        const res = await fetch(API_CONFIG.getApiUrl('/unified-auth/enquiry/recaptcha-site-key'));
        const data = await res.json();
        const siteKey = (data.site_key || '').trim();
        if (cancelled || !siteKey) return;
        setRecaptchaSiteKey(siteKey);
        if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
          window.grecaptcha.ready(() => { if (!cancelled) setRecaptchaReady(true); });
          return;
        }
        if (document.querySelector('script[src*="google.com/recaptcha/api.js"]')) {
          if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
            window.grecaptcha.ready(() => { if (!cancelled) setRecaptchaReady(true); });
          } else {
            setRecaptchaReady(true);
          }
          return;
        }
        const script = document.createElement('script');
        script.src = `${RECAPTCHA_V2_SCRIPT_BASE}?onload=${RECAPTCHA_ONLOAD_NAME}&render=explicit`;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      } catch {
        // No captcha if endpoint fails (e.g. CORS or offline)
      }
    };
    loadRecaptcha();
    return () => {
      cancelled = true;
      delete window[RECAPTCHA_ONLOAD_NAME];
    };
  }, []);

  // Render v2 checkbox widget when container and API are ready (use grecaptcha.ready)
  useEffect(() => {
    if (!recaptchaSiteKey || !recaptchaReady || !recaptchaContainerRef.current) return;
    if (recaptchaWidgetIdRef.current != null) return; // already rendered
    const renderWidget = () => {
      if (!window.grecaptcha || typeof window.grecaptcha.render !== 'function' || !recaptchaContainerRef.current) return;
      if (recaptchaWidgetIdRef.current != null) return;
      try {
        const id = window.grecaptcha.render(recaptchaContainerRef.current, {
          sitekey: recaptchaSiteKey,
          theme: 'light',
          size: 'normal'
        });
        recaptchaWidgetIdRef.current = id;
      } catch (e) {
        console.warn('reCAPTCHA render failed:', e);
      }
    };
    if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
      window.grecaptcha.ready(renderWidget);
    } else {
      renderWidget();
    }
  }, [recaptchaSiteKey, recaptchaReady]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.website) {
      setError('Invalid submission. Please try again.');
      return;
    }
    const elapsedSec = (Date.now() - formLoadTimeRef.current) / 1000;
    if (elapsedSec < MIN_FORM_SECONDS) {
      setError('Please take a moment to complete the form before submitting.');
      return;
    }

    if (!formData.email || !formData.name_company || !formData.message) {
      setError('Please fill in all fields');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    let recaptchaToken = null;
    if (recaptchaSiteKey && window.grecaptcha && recaptchaWidgetIdRef.current != null) {
      recaptchaToken = window.grecaptcha.getResponse(recaptchaWidgetIdRef.current);
      if (!recaptchaToken || !recaptchaToken.trim()) {
        setError('Please complete the captcha verification.');
        return;
      }
    }

    setLoading(true);
    try {
      const body = {
        email: formData.email,
        name_company: formData.name_company,
        message: formData.message,
        form_load_time: formLoadTimeRef.current,
        ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {})
      };
      const response = await fetch(
        API_CONFIG.getApiUrl('/unified-auth/enquiry'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }
      );
      let data = {};
      try {
        data = await response.json();
      } catch {
        data = { detail: 'Invalid response from server.' };
      }

      if (response.ok) {
        setSubmitted(true);
        setFormData({ email: '', name_company: '', message: '' });
      } else {
        const msg = data.detail || 'Failed to submit enquiry. Please try again.';
        setError(response.status === 429 ? 'You have reached the maximum number of submissions. Please try again later.' : msg);
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="enquiry-form-container">
        <div className="enquiry-form-content">
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h2>Thank You!</h2>
            <p>Your enquiry has been submitted successfully.</p>
            <p>We'll get back to you as soon as possible.</p>
            <button
              onClick={() => {
                setSubmitted(false);
                navigate('/');
              }}
              className="back-button"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="enquiry-form-container">
      <Helmet>
        <title>Contact Us | PayCIS</title>
        <meta name="description" content="Get in touch with PayCIS. Send your questions about CIS payments, subcontractor management, and HMRC compliance." />
        <meta property="og:title" content="Contact Us | PayCIS" />
      </Helmet>
      <div className="enquiry-form-content">
        <div className="enquiry-form-header">
          <h1>Contact Us</h1>
          <p>Have a question? We'd love to hear from you. Send us a message and we'll respond as soon as possible.</p>
        </div>

        <form onSubmit={handleSubmit} className="enquiry-form">
          <div className="form-group">
            <label style={{ color: 'white' }} htmlFor="email">Email *</label>
            <input
              style={{ color: 'white' }}
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your.email@example.com"
              autoComplete="email"
              required
              disabled={loading}
            />
          </div>

          {/* Honeypot – hidden from users; bots that fill it are rejected */}
          <div style={{ position: 'absolute', left: '-9999px', top: 0, width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input
              type="text"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <div className="form-group">
            <label style={{ color: 'white' }} htmlFor="name_company">Name / Company *</label>
            <input
              style={{ color: 'white' }}
              type="text"
              id="name_company"
              name="name_company"
              value={formData.name_company}
              onChange={handleChange}
              placeholder="Your name or company name"
              autoComplete="name"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label style={{ color: 'white' }} htmlFor="message">Message *</label>
            <textarea
              style={{ color: 'white' }}
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us how we can help..."
              rows="6"
              autoComplete="off"
              required
              disabled={loading}
            />
          </div>

          {recaptchaSiteKey && (
            <div className="form-group">
              <div ref={recaptchaContainerRef} className="recaptcha-container" />
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-actions">
            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit Enquiry'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="cancel-button"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EnquiryForm;

